import { cx } from '@/styled-system/css'
import {
  createContext,
  createElement,
  forwardRef,
  useContext,
  type ComponentProps,
  type ElementType,
  type JSX,
  type ForwardedRef,
} from 'react'

type GenericProps = Record<string, unknown>
type StyleRecipe = {
  (props?: GenericProps): Record<string, string>
  splitVariantProps?: (props: GenericProps) => any
}
type StyleSlot<R extends StyleRecipe> = keyof ReturnType<R>
type StyleSlotRecipe<R extends StyleRecipe> = Record<StyleSlot<R>, string>
type StyleVariantProps<R extends StyleRecipe> = Parameters<R>[0]
type CombineProps<T, U> = Omit<T, keyof U> & U

export interface ComponentVariants<
  T extends ElementType,
  R extends StyleRecipe,
> {
  (props: CombineProps<ComponentProps<T>, StyleVariantProps<R>>): JSX.Element
}

export const createStyleContext = <R extends StyleRecipe>(recipe: R) => {
  const StyleContext = createContext<StyleSlotRecipe<R> | null>(null)

  const withProvider = <T extends ElementType>(
    Component: T,
    slot?: StyleSlot<R>
  ): ComponentVariants<T, R> => {
    const StyledComponent = forwardRef((props: ComponentProps<T>, ref) => {
      // Add a safety check for splitVariantProps
      let variantProps = {} as GenericProps
      let otherProps = { ...props } as Record<string, any>
      
      if (recipe.splitVariantProps) {
        // Only call splitVariantProps if it exists
        try {
          [variantProps, otherProps] = recipe.splitVariantProps(props)
        } catch (error) {
          console.warn('Error in splitVariantProps:', error)
        }
      }
      
      // Safely call the recipe function with defensive error handling
      let slotStyles = {} as Record<string, string>
      try {
        if (typeof recipe === 'function') {
          slotStyles = recipe(variantProps) as StyleSlotRecipe<R>
        } else {
          console.warn('Recipe is not a function', recipe)
        }
      } catch (error) {
        console.warn('Error calling recipe function:', error)
      }
      
      // Ensure slotStyles is an object
      if (!slotStyles || typeof slotStyles !== 'object') {
        slotStyles = {} as Record<string, string>
      }
      
      // Get className safely
      const slotClassName = slot && slotStyles[slot as any] ? slotStyles[slot as any] : ''
      const propClassName = otherProps.className || ''
      
      return (
        <StyleContext.Provider value={slotStyles as StyleSlotRecipe<R>}>
          <Component
            ref={ref}
            {...otherProps}
            className={cx(slotClassName, propClassName)}
          />
        </StyleContext.Provider>
      )
    })
    
    const componentName = typeof Component === 'string' 
      ? Component 
      : (Component as any).displayName || (Component as any).name || 'Component'
    
    StyledComponent.displayName = `${componentName}WithProvider`
    
    return StyledComponent as unknown as ComponentVariants<T, R>
  }

  const withContext = <T extends ElementType>(
    Component: T,
    slot?: StyleSlot<R>
  ): T => {
    if (!slot) return Component
    
    const StyledComponent = forwardRef((props: ComponentProps<T>, ref: ForwardedRef<any>) => {
      const slotStyles = useContext(StyleContext) || {} as StyleSlotRecipe<R>
      
      // Get className safely
      const slotClassName = slot && slotStyles[slot as any] ? slotStyles[slot as any] : ''
      const propClassName = (props as any).className || ''
      
      return createElement(Component, {
        ...(props as any),
        className: cx(slotClassName, propClassName),
        ref,
      })
    })
    
    const componentName = typeof Component === 'string' 
      ? Component 
      : (Component as any).displayName || (Component as any).name || 'Component'
    
    StyledComponent.displayName = `${componentName}WithContext`
    
    return StyledComponent as unknown as T
  }

  return { withProvider, withContext }
}
